import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export interface MotivatorNotification {
  id: string
  user_id: number
  type: string
  title: string
  message: string
  read: boolean
  created_at: string
  data?: Record<string, any>
}

export interface MotivatorNotificationsApi {
  notifications: MotivatorNotification[]
  unreadCount: number
  loading: boolean
  fetchNotifications: () => Promise<void>
  markAsRead: (notificationId: string) => Promise<void>
  markAllAsRead: () => Promise<void>
}

/**
 * The ONE notifications feed, published by the app-root provider.
 *
 * Defect fix 2026-08-29: Header and MobileLayout each opened their own 60s
 * poller against /api/orchestrator/notifications*. Layout renders one OR the
 * other, so they were not two live pollers — but crossing the mobile
 * breakpoint threw the feed away and refetched it, and the bell's data now has
 * one owner. The hook's public shape is unchanged.
 */
export const MotivatorNotificationsContext = createContext<MotivatorNotificationsApi | null>(null)

/** The fetching implementation — mounted once by the provider. */
export function useMotivatorNotificationsSource(
  pollIntervalMs: number = 60000,
  enabled: boolean = true,
): MotivatorNotificationsApi {
  const [notifications, setNotifications] = useState<MotivatorNotification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading] = useState(false)

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch('/api/orchestrator/notifications?limit=20&unread_only=false', {
        credentials: 'include',
      })
      if (res.ok) {
        const data = await res.json()
        if (data.success) {
          setNotifications(data.notifications || [])
          setUnreadCount(data.unread_count || 0)
        }
      }
    } catch (err) {
      // Silent fail -- notifications are non-critical
    }
  }, [])

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch('/api/orchestrator/notifications/unread-count', {
        credentials: 'include',
      })
      if (res.ok) {
        const data = await res.json()
        setUnreadCount(data.unread_count || 0)
      }
    } catch {
      // Silent fail
    }
  }, [])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await fetch(`/api/orchestrator/notifications/${notificationId}/read`, {
        method: 'POST',
        credentials: 'include',
      })
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch {
      // Silent fail
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      await fetch('/api/orchestrator/notifications/read-all', {
        method: 'POST',
        credentials: 'include',
      })
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch {
      // Silent fail
    }
  }, [])

  // Initial fetch
  useEffect(() => {
    if (!enabled) return
    fetchNotifications()
  }, [enabled, fetchNotifications])

  // Poll for unread count
  useEffect(() => {
    if (!enabled) return
    const interval = setInterval(fetchUnreadCount, pollIntervalMs)
    return () => clearInterval(interval)
  }, [enabled, fetchUnreadCount, pollIntervalMs])

  return useMemo(() => ({
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  }), [notifications, unreadCount, loading, fetchNotifications, markAsRead, markAllAsRead])
}

/**
 * Public hook — unchanged call shape. Reads the app-root feed when one is
 * mounted; falls back to its own poller otherwise.
 */
export function useMotivatorNotifications(pollIntervalMs: number = 60000): MotivatorNotificationsApi {
  const shared = useContext(MotivatorNotificationsContext)
  const local = useMotivatorNotificationsSource(pollIntervalMs, shared === null)
  return shared ?? local
}
